import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { IRecipeDevices, IRecipeOverview, SdRecipeOverviewComponent } from '@seb/web-design-system/recipe-overview';
import { BrandsEnum, BrandThemesEnum, PositionEnum } from '@seb/web-design-system/core';
import { IDevice } from '@seb/web-design-system/devices';
import { RecipeUserEnum } from '@seb/web-design-system/recipe-user';
import { Product } from '@spartacus/core';
import { ApplianceRange, Recipe } from '@seb/web-common/core/models';
import { HeadingParagraphMapper, RecipeMapper } from '@seb/web-common/core/mappers';
import { RecipeService, TrackingService } from '@seb/web-common/core/services';
import { getMediaByFormat } from '@seb/web-common/core/utils';

@Component({
  selector: 'wc-recipe-overview',
  templateUrl: './recipe-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdRecipeOverviewComponent
  ]
})
export class RecipeOverviewComponent {
  recipeOverview: IRecipeOverview;
  currentRecipe: Recipe;
  isReworked = false;
  snippetsRecipe;

  @Input() set recipe(currentRecipe: Recipe) {
    if (!!currentRecipe && currentRecipe?.fid.functionalId !== this.currentRecipe?.fid.functionalId) {
      this.currentRecipe = currentRecipe;
      this.setRecipeOverview(this.currentRecipe);
    }
  }

  @Input() isLoading = true;

  @Input() brand = BrandsEnum.default;

  @Output() hasLoaded = new EventEmitter<void>();

  @Output()
  printClicked: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  reviewsClicked = new EventEmitter<number>();

  @Output()
  updateYield = new EventEmitter<number>();

  constructor(
    private recipeMapper: RecipeMapper,
    private recipeService: RecipeService,
    private translateService: TranslateService,
    private cdRef: ChangeDetectorRef,
    private headingParagraphMapper: HeadingParagraphMapper,
    private trackingService: TrackingService
  ) {}

  setRecipeOverview(recipe: Recipe) {
    this.getRecipeDevices(recipe).subscribe((recipeDevices) => {
      this.recipeOverview = this.recipeMapper.recipeToRecipeOverview(recipe, recipeDevices);
      this.snippetsRecipe = !!recipe && !!this.recipeOverview ? this.toSnippets(this.recipeOverview, recipe) : {};
      this.isReworked = recipe?.isReworked;
      this.hasLoaded.emit();
      this.cdRef.markForCheck();
    });
  }

  getRecipeDevices(recipe): Observable<IRecipeDevices | null> {
    let devices = [];
    let maxProductDisplayed = 5;
    if (recipe?.ranges?.length > 0) {
      const uniqueRanges = [
        ...new Set(
          recipe.ranges?.filter((range: ApplianceRange) => !!range?.key).map((range: ApplianceRange) => range.key)
        )
      ];
      return this.recipeService.searchProductsByRangeAndModels(recipe.ranges).pipe(
        catchError(() => of(null)),
        map((result) => {
          if (result) {
            if (result?.pagination.totalResults > 5) {
              maxProductDisplayed = 4;
            }
            const sortedProducts = this.sortProducts(result?.products, uniqueRanges, maxProductDisplayed).filter(
              (product) => product.images
            );
            const richParagraphData = {
              heading: {
                headline: this.translateService.instant('recipes.devices.range-disclaimer')

              }
            };
            const push = {
              push: {
                media: getMediaByFormat(sortedProducts?.[0]?.primaryImages, 'main') ?? sortedProducts[0]?.images['PRIMARY']['main'],
                richParagraph: {
                  links: [
                    {
                      url: `/search?target=product&query=:relevance:range:${uniqueRanges.join(
                        ':range:'
                      )}&isAccessorySearch=true`,
                      external: true
                    }
                  ],
                  heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData)
                }
              },
              mediaConfig: PositionEnum.center,
              themesConfig: BrandThemesEnum.brand
            };
            devices = sortedProducts.map((product) => ({
              label: product.name,
              link: {
                url: product.url,
                linkName: product.name,
                external: true
              },
              media: getMediaByFormat(product?.primaryImages, 'main') ?? product?.images?.['PRIMARY']?.['main']
            }));

            return {
              devices: devices,
              push: push ? push : null,
              maxProductDisplayed: maxProductDisplayed
            };
          } else {
            return null;
          }
        })
      );
    } else {
      return of(null);
    }
  }

  sortProducts(products: Product[], uniqueRanges, maxProductDisplayed): Product[] {
    const rangesWithOnSale: { key: string; onSale: boolean }[] = uniqueRanges.map((range) => ({
      key: range,
      onSale:
        products.findIndex(
          (product) => product.commercialCode.toUpperCase().startsWith(range.toUpperCase()) && product.onSale
        ) > -1
    }));
    rangesWithOnSale.sort((r1, r2) => Number(r2.onSale) - Number(r1.onSale));

    const productList = [...products];
    productList.sort((p1, p2) => Number(p2.onSale) - Number(p1.onSale));

    const sortedProducts: Product[] = [];
    let loopIndex = 0;
    while (sortedProducts.length < maxProductDisplayed && productList.length > 0 && loopIndex < maxProductDisplayed) {
      rangesWithOnSale.forEach((range) => {
        const productInRangeIndex = productList.findIndex((product) =>
          product.commercialCode.toUpperCase().startsWith(range.key.toUpperCase())
        );
        if (productInRangeIndex > -1) {
          sortedProducts.push(...productList.splice(productInRangeIndex, 1));
        }
      });
      loopIndex++;
    }

    sortedProducts.push(...productList);
    return sortedProducts;
  }

  onPrintClick() {
    this.printClicked.emit();
  }

  onReviewsClicked(nbReviews: number) {
    this.reviewsClicked.emit(nbReviews);
  }

  onDeviceClick(event: IDevice) {
    this.trackingService.sendEvent('clickproductRecipe', {
      productName: event.label ? event.label : ''
    });
  }

  onDevicePushClick() {
    this.trackingService.sendEvent('clickproductDiscover', {});
  }

  onUpdateValue(nbYield: number) {
    this.updateYield.emit(nbYield);
  }

  toSnippets(recipeOverview, recipe) {
    const recipeAuthorObj = recipeOverview.user;
    const recipeInstructions = recipeOverview.recipe?.steps?.map((step) => {
      return {
        '@type': 'HowToStep',
        text: step.description
      };
    });
    const recipeIngredients = recipeOverview.recipe?.ingredients?.map((ingredient) => {
      const quantity = ingredient.quantity ? ingredient.quantity : '';
      const unit = ingredient.unit?.abbreviation ? ingredient.unit?.abbreviation : '';
      const ingredientInfo = quantity + '' + unit + ' ' + ingredient.name;
      return ingredientInfo.trim();
    });
    const snippets = {
      '@context': 'https://schema.org/',
      '@type': 'Recipe',
      brand: {
        '@type': 'Brand',
        name: recipe.brand.key
      },
      name: recipeOverview.recipe.title,
      image: recipeOverview.media ? [recipeOverview.media.url.split('?')[0]] : [],
      author: recipeAuthorObj
        ? {
          '@type': recipeAuthorObj.type === RecipeUserEnum.BRAND ? 'Organization' : 'Person',
          name: recipeAuthorObj.text
        }
        : {},
      recipeIngredient: recipeIngredients ? recipeIngredients : [],
      recipeInstructions: recipeInstructions ? recipeInstructions : [],
      totalTime: recipe.durations?.totalTime ? 'PT' + recipe.durations.totalTime + 'M' : '',
      recipeYield: recipeOverview.recipe.yield.quantity,
      aggregateRating: recipeOverview.recipe?.communityTopRecipe?.raters > 0
        ? {
          '@type': 'AggregateRating',
          ratingValue: Math.ceil(recipeOverview.recipe?.communityTopRecipe.rating * 10) / 10,
          ratingCount: recipeOverview.recipe?.communityTopRecipe.raters
        }
        : {}
    };
    return snippets;
  }
}
